import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Typography } from "@/components/ui/typography"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { SETTINGS_PATHS } from "@/features/settings/paths"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  AlertTriangle,
  ArrowRight,
  Boxes,
  FileText,
  Loader2,
  PackagePlus,
  Plus,
  Users,
} from "lucide-react"
import { useMemo, useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import { useArticleList } from "../../../api/articlesApi"
import {
  CONFORMITY_EMPTY_REASON,
  documentTooLargeDetail,
  noFrameworksRefused,
  scopeTooLargeDetail,
  useConformityRegulationOptions,
  useConformityV2Preview,
  useCreateSellThroughProducts,
  useGenerateConformityV2,
  type ConformityV2Framework,
  type ConformityV2Target,
} from "../../../api/conformityApi"
import {
  useCustomerProductMappings,
  useUpsertCustomerProductMapping,
} from "../../../api/customersApi"
import {
  DEFAULT_TEMPLATE_KEY,
  pickableTemplates,
  useDeclarationTemplates,
} from "../../../api/declarationTemplateApi"
import { ORG_REGULATIONS_SETTINGS_ROUTE } from "../../../api/regulationSettingsApi"
import {
  DEFAULT_SCOPE_MAX_SUBJECTS,
  DEFAULT_SCOPE_WARN_SUBJECTS,
  useTemplateSettings,
} from "../../../api/templateSettingsApi"
import { useCanViewOrgRegulations } from "../../../hooks/useCanViewOrgRegulations"
import {
  REGULATION_KEY_DEPENDENCIES,
  withKeyPrerequisites,
} from "../../../utils/inquiryScope"
import { EntityListRowContent } from "../../shared/EntityListRowContent"
import { McPill } from "../../shared/McPill"
import { ModuleHeader } from "../../shared/ModuleHeader"
import { InquiryRegulationPicker } from "../../supplier-declarations/inquiry/InquiryRegulationPicker"
import { CustomerPickerTable } from "./editor/CustomerPickerTable"
import { EntityPickerTable } from "./editor/EntityPickerTable"
import { ProductPickerTable } from "./editor/ProductPickerTable"
import {
  frameworkBlockedLabel,
  frameworkClassPill,
} from "./frameworkPresentation"
import { ScopeTile, Step, flipMember } from "./scopePicker"

const MC_BASE = "/material-compliance"

/** Customer Studio → Conformity → Generate, v2 (spec §5, Q1/Q19).
 *
 * Two scopes, ONE document each: `customer` (single customer, subjects are the
 * mapped products) and `products` (multi-select, optional one-document-per-
 * product). A regulation picker (TUR-1733) chooses what the declaration covers,
 * preselected with the org's set plus the customer's own regulations (or a
 * customer request's, from its link); each becomes a deselectable block in the
 * editor. The Products tab carries the sell-through action that turns
 * purchased articles into products (Q19). */
export function ConformityGenerateModuleV2() {
  const navigate = useNavigate()
  const { translate } = useTranslation()
  const [searchParams] = useSearchParams()

  // v1 deep-links say scope=product; an article deep-link (master data) lands
  // on the Products tab with the sell-through picker open for that article.
  const initialScope = searchParams.get("scope")
  const initialCustomerId =
    searchParams.get("customerId")?.split(",").filter(Boolean)[0] ?? null
  const initialProductIds =
    searchParams.get("productId")?.split(",").filter(Boolean) ?? []
  const initialArticleIds =
    searchParams.get("articleId")?.split(",").filter(Boolean) ?? []

  const [requestedScope, setScope] = useState<
    "customer" | "products" | "general"
  >(
    initialScope === "general"
      ? "general"
      : initialScope === "product" ||
          initialScope === "products" ||
          initialScope === "article"
        ? "products"
        : "customer",
  )
  // Kill switch (TUR-1573): while MC_CONFORMITY_GENERAL is off the general
  // tile is hidden and a ?scope=general link (or a stale selection) falls
  // back to the customer scope. Derived rather than reset, so a real
  // general deep link still works once the flags have finished loading.
  const generalEnabled = useFeatureFlag("MC_CONFORMITY_GENERAL")
  const scope =
    requestedScope === "general" && !generalEnabled
      ? "customer"
      : requestedScope
  const [customerId, setCustomerId] = useState<string | null>(initialCustomerId)
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    () => new Set(initialProductIds),
  )
  const [productDescription, setProductDescription] = useState("")
  const [onePerSubject, setOnePerSubject] = useState(false)
  // Phase-3 template switcher: only shown when the org has two layouts to
  // choose from (`pickableTemplates`); null = the org default, resolved
  // server-side.
  const templatesEnabled = useFeatureFlag("MC_DECLARATION_TEMPLATES")
  const templates = useDeclarationTemplates({ enabled: templatesEnabled })
  // The no-frameworks "open regulations" link lands on the Settings card;
  // offer it only to users who can see that card.
  const canOpenRegulations = useCanViewOrgRegulations()
  const publishedTemplates = useMemo(
    () => pickableTemplates(templates.data ?? []),
    [templates.data],
  )
  const [templateKey, setTemplateKey] = useState<string | null>(null)
  const [sellThroughOpen, setSellThroughOpen] = useState(
    initialScope === "article" && initialArticleIds.length > 0,
  )
  const [sellThroughIds, setSellThroughIds] = useState<Set<string>>(
    () => new Set(initialArticleIds),
  )
  const [addProductOpen, setAddProductOpen] = useState(false)
  const [productsToLink, setProductsToLink] = useState<Set<string>>(new Set())

  // TUR-1733: the regulations this declaration covers. Until the user changes
  // the pick it follows the preselection, which moves with the scope and the
  // customer: the org's set plus the customer's own (customer scope only), or a
  // customer request's regulations handed over in the link (`regulations=`,
  // empty when the request named none). A key the declaration cannot carry
  // (outside both sets and not declarable) is dropped, never sent. A changed
  // pick is held against each target's options too: another customer has other
  // own regulations, so what the new target cannot carry leaves the pick.
  const regulationsParam = searchParams.get("regulations")
  const regulationOptions = useConformityRegulationOptions(
    scope === "customer" ? customerId : null,
  )
  const [pickedRegulations, setPickedRegulations] = useState<string[] | null>(
    null,
  )
  // What a declaration can carry (the server's rule for a pick): the org set and
  // the customer's own regulations, or any other that can be declared, and only
  // when every prerequisite can be carried too (PFAS needs REACH Annex XVII).
  const carriable = useMemo(() => {
    const options = regulationOptions.data
    if (!options) return null
    const allowed = new Set(
      options
        .filter((o) => o.pickable || o.inOrgSet || o.inCustomerSet)
        .map((o) => o.key),
    )
    return new Set(
      [...allowed].filter((key) =>
        (REGULATION_KEY_DEPENDENCIES[key] ?? []).every((dep) =>
          allowed.has(dep),
        ),
      ),
    )
  }, [regulationOptions.data])
  // What the picker offers: a regulation that cannot be carried is greyed out, so
  // PFAS cannot be ticked while its Annex XVII cannot be declared.
  const pickerOptions = useMemo(
    () =>
      regulationOptions.data?.map((o) =>
        carriable?.has(o.key) ? o : { ...o, pickable: false },
      ),
    [regulationOptions.data, carriable],
  )
  const preselectedRegulations = useMemo(() => {
    const options = regulationOptions.data
    if (!options || !carriable) return null
    const requested = new Set(regulationsParam?.split(",").filter(Boolean))
    const named =
      regulationsParam !== null
        ? options.filter((o) => requested.has(o.key))
        : options.filter(
            (o) => o.inOrgSet || (scope === "customer" && o.inCustomerSet),
          )
    // Closed like the server closes a pick (PFAS brings REACH Annex XVII), so the
    // picker shows what is declared and an unrelated untick cannot drop PFAS for a
    // missing prerequisite. What cannot be carried goes, with what needs it.
    return withKeyPrerequisites(named.map((o) => o.key)).filter((key) =>
      carriable.has(key),
    )
  }, [regulationOptions.data, carriable, regulationsParam, scope])
  // The reviewer's pick (held against this target's options), else the
  // preselection (still there from the cache when a refetch fails). undefined: the
  // options could not be read and no selection was linked, so nothing is sent and
  // the server keeps its own set; null: nothing to go on yet (loading, or a linked
  // selection that cannot be resolved without the options), so nothing is
  // evaluated or generated. A pick never travels to a target whose options have
  // not arrived or could not be read: they decide what it can carry.
  const regulationKeys =
    pickedRegulations !== null
      ? carriable
        ? pickedRegulations.filter((key) => carriable.has(key))
        : null
      : (preselectedRegulations ??
        (regulationOptions.isError && regulationsParam === null
          ? undefined
          : null))

  // The PREVIEW target deliberately leaves the description out for the general
  // scope. A general evaluation does not depend on it at all, but this object
  // keys useConformityV2Preview, so carrying the text here made every keystroke
  // a new query key, a new request and a loading flash of the framework box and
  // the Generate button (Ferdi's live test: "dann flackert die Box drunter und
  // der Button"). The description reaches the server only on generate.
  const target: ConformityV2Target | null = useMemo(() => {
    let base: ConformityV2Target | null
    if (scope === "customer") {
      base = customerId ? { scope: "customer", customerId } : null
    } else if (scope === "general") {
      base = { scope: "general", customerId }
    } else {
      base =
        selectedProductIds.size > 0
          ? { scope: "products", subjectIds: [...selectedProductIds] }
          : null
    }
    if (!base || regulationKeys === undefined) return base
    // Nothing picked (or the options still loading): nothing to evaluate.
    if (regulationKeys === null || regulationKeys.length === 0) return null
    return { ...base, regulationKeys }
  }, [scope, customerId, selectedProductIds, regulationKeys])

  // Empty is legitimate (all products); 1 or 2 characters is a half-typed family
  // name. Blocking it here turns a generic server 422 into an inline hint.
  const descriptionTooShort =
    scope === "general" &&
    productDescription.trim().length > 0 &&
    productDescription.trim().length < 3

  // What generate actually sends: the same scope plus the optional family text.
  // Empty means "all our products", so it is sent as null rather than ''.
  const generateTarget: ConformityV2Target | null = useMemo(() => {
    if (target?.scope !== "general") return target
    if (descriptionTooShort) return null
    return { ...target, productDescription: productDescription.trim() || null }
  }, [target, productDescription, descriptionTooShort])

  const templateSettings = useTemplateSettings()
  const warnAt =
    templateSettings.data?.scopeWarnSubjects ?? DEFAULT_SCOPE_WARN_SUBJECTS
  const maxSubjects =
    templateSettings.data?.scopeMaxSubjects ?? DEFAULT_SCOPE_MAX_SUBJECTS
  // WP1 scope guard. The size has to be known BEFORE the preview, not from its
  // result: the whole point is to avoid the evaluation. Customer scope reads the
  // mapping list (one cheap query, already cached by the picker); products scope
  // knows its own selection.
  const mappings = useCustomerProductMappings(
    scope === "customer" ? customerId : null,
  )
  const scopeSize =
    scope === "customer"
      ? (mappings.data?.length ?? null)
      : scope === "general"
        ? 0
        : selectedProductIds.size
  // B3: while the mapping list is in flight `scopeSize` is null, so the guard
  // cannot fire yet — firing the preview here starts the very evaluation the
  // guard exists to avoid, on every cold cache and every deep link.
  const sizePending =
    scope === "customer" &&
    !!customerId &&
    mappings.data === undefined &&
    !mappings.isError
  const scopeRefused = scopeSize !== null && scopeSize > maxSubjects
  const scopeWarned =
    scopeSize !== null && !scopeRefused && scopeSize > warnAt ? scopeSize : 0

  const preview = useConformityV2Preview(
    scopeRefused || sizePending ? null : target,
  )
  const generate = useGenerateConformityV2()
  const sellThrough = useCreateSellThroughProducts()
  const linkProduct = useUpsertCustomerProductMapping()

  const evaluation = preview.data ?? null
  // Memoized: `mappedProductIds` derives from it, and a fresh [] every render
  // would rebuild that Set on every keystroke.
  const subjects = useMemo(() => evaluation?.subjects ?? [], [evaluation])
  const frameworks = evaluation?.frameworks ?? []
  const noMappedProducts =
    evaluation?.emptyReason === CONFORMITY_EMPTY_REASON.noCustomerProducts
  // The org subscribes to no regulation (mc_org_regulations empty, nothing to
  // infer from). Before 2026-09-22 this rendered as a blank list with an
  // enabled Generate button that persisted an empty draft.
  const noFrameworks =
    evaluation?.emptyReason === CONFORMITY_EMPTY_REASON.noFrameworksConfigured
  // Either we refused locally, or the server did (a scope that grew between the
  // mapping read and the preview, or a products scope the picker didn't bound).
  const scopeRefusal = scopeRefused
    ? { count: scopeSize as number, limit: maxSubjects }
    : scopeTooLargeDetail(preview.error)
  const mappedProductIds = useMemo(
    () => new Set(subjects.map((s) => s.subjectId)),
    [subjects],
  )

  const documentCount =
    scope === "products" && onePerSubject
      ? selectedProductIds.size
      : target
        ? 1
        : 0

  async function onCreateSellThrough() {
    if (sellThroughIds.size === 0 || sellThrough.isPending) return
    try {
      const { productIds } = await sellThrough.mutateAsync([...sellThroughIds])
      setSelectedProductIds((prev) => new Set([...prev, ...productIds]))
      setSellThroughOpen(false)
      setSellThroughIds(new Set())
      toast.success(
        translate("MC_CS_CONF_V2_SOLD_AS_IS_TOAST_OK", {
          n: productIds.length,
        }),
      )
    } catch {
      toast.error(translate("MC_CS_CONF_V2_SOLD_AS_IS_TOAST_ERR"))
    }
  }

  async function onLinkProducts() {
    if (!customerId || productsToLink.size === 0 || linkProduct.isPending)
      return
    try {
      // Writes mc_customer_products — a permanent master-data link, not a
      // per-document choice — then the preview re-evaluates with the new
      // subject.
      for (const productId of productsToLink) {
        await linkProduct.mutateAsync({
          customerId,
          productId,
          customerProductNumber: null,
        })
      }
      setAddProductOpen(false)
      setProductsToLink(new Set())
      await preview.refetch()
      toast.success(
        translate("MC_CS_CONF_V2_ADD_PRODUCT_TOAST", {
          n: productsToLink.size,
        }),
      )
    } catch {
      toast.error(translate("MC_CONF_V2_ACTION_FAILED"))
    }
  }

  async function onGenerate() {
    if (!generateTarget || generate.isPending) return
    try {
      const { declarationIds } = await generate.mutateAsync({
        ...generateTarget,
        onePerSubject: scope === "products" && onePerSubject,
        templateKey,
      })
      toast.success(
        translate("MC_CS_CONF_V2_TOAST_OK", { n: declarationIds.length }),
      )
      // Straight into the editor (per Haseeb, 2026-09-10); a per-product
      // batch keeps the ?batch= pager.
      const batch =
        declarationIds.length > 1 ? `?batch=${declarationIds.join(",")}` : ""
      navigate(
        `${MC_BASE}/customer-studio/conformity/edit/${declarationIds[0]}${batch}`,
      )
    } catch (error) {
      const tooLarge = scopeTooLargeDetail(error)
      if (tooLarge) {
        // The copy tells the user to raise the limit in settings, so take them
        // there — the same gesture the editor uses for a missing signatory.
        toast.error(
          translate("MC_CS_CONF_V2_SCOPE_TOO_LARGE", {
            count: String(tooLarge.count),
            limit: String(tooLarge.limit),
          }),
          {
            action: {
              label: translate("MC_CONF_V2_FIX_OPEN_SETTINGS"),
              onClick: () => navigate(SETTINGS_PATHS.conformityDeclarations),
            },
          },
        )
        return
      }
      const tooManyRows = documentTooLargeDetail(error)
      if (tooManyRows) {
        toast.error(
          translate("MC_CS_CONF_V2_DOCUMENT_TOO_LARGE", {
            count: String(tooManyRows.count),
            limit: String(tooManyRows.limit),
          }),
        )
        return
      }
      // The preview disables the button for this, so a 409 here means the org's
      // Regelwerk set changed between preview and generate. Same copy either way.
      if (noFrameworksRefused(error)) {
        toast.error(translate("MC_CS_CONF_V2_NO_FRAMEWORKS"))
        return
      }
      toast.error(translate("MC_CS_CONF_GEN_TOAST_ERR"))
    }
  }

  const activeCount =
    scope === "customer"
      ? customerId
        ? 1
        : 0
      : scope === "general"
        ? target
          ? 1
          : 0
        : selectedProductIds.size

  return (
    <div
      data-agent-target="conformity.new"
      className="flex min-h-full flex-col gap-6 p-6"
    >
      <ModuleHeader
        title={translate("MC_CS_CONF_GEN_TITLE")}
        helpArticle={{
          categoryId: "customer-studio",
          articleId: "customer.conformity",
        }}
        subtitle={
          scope === "customer"
            ? translate("MC_CS_CONF_V2_PICK_ONE_CUSTOMER")
            : scope === "general"
              ? translate("MC_CS_CONF_V2_SCOPE_GENERAL_SUB")
              : translate("MC_CS_CONF_V2_SCOPE_PRODUCTS_SUB")
        }
      />

      <Step n={1} done heading={translate("MC_CS_CONF_GEN_STEP1_HEADING")}>
        <div
          className={`grid gap-2.5 ${generalEnabled ? "grid-cols-3" : "grid-cols-2"}`}
        >
          <ScopeTile
            active={scope === "customer"}
            icon={<Users size={14} />}
            title={translate("MC_CS_CONF_GEN_SCOPE_BY_CUSTOMER")}
            subtitle={translate("MC_CS_CONF_V2_SCOPE_CUSTOMER_SUB")}
            onClick={() => setScope("customer")}
          />
          <ScopeTile
            active={scope === "products"}
            icon={<Boxes size={14} />}
            title={translate("MC_CS_CONF_V2_SCOPE_PRODUCTS")}
            subtitle={translate("MC_CS_CONF_V2_SCOPE_PRODUCTS_SUB")}
            onClick={() => setScope("products")}
          />
          {generalEnabled && (
            <ScopeTile
              active={scope === "general"}
              icon={<FileText size={14} />}
              title={translate("MC_CS_CONF_V2_SCOPE_GENERAL")}
              subtitle={translate("MC_CS_CONF_V2_SCOPE_GENERAL_SUB")}
              onClick={() => setScope("general")}
            />
          )}
        </div>
      </Step>

      <Step
        n={2}
        done={activeCount > 0}
        heading={
          scope === "customer"
            ? translate("MC_CS_CONF_GEN_STEP2_HEADING")
            : scope === "general"
              ? translate("MC_CS_CONF_V2_GENERAL_ADDRESSEE")
              : translate("MC_CS_CONF_GEN_STEP2_HEADING_PRODUCT")
        }
      >
        <div
          data-testid="conformity-generate-grid"
          className="grid grid-cols-[minmax(0,1fr)_320px] items-start gap-6"
        >
          <div className="min-w-0">
            <div className="mb-3 flex items-center justify-between">
              {scope === "products" ? (
                <button
                  type="button"
                  onClick={() => setSellThroughOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-md border border-grey4 bg-grey2 px-2.5 py-1.5 text-[12px] font-medium text-white transition-colors hover:border-grey5"
                >
                  <PackagePlus size={13} className="text-yellow1" />
                  {translate("MC_CS_CONF_V2_SOLD_AS_IS")}
                </button>
              ) : (
                <span />
              )}
              <span className="whitespace-nowrap text-[12px] text-grey5">
                <b className="text-white">{activeCount}</b>{" "}
                {translate("MC_CS_CONF_GEN_SELECTED")}
              </span>
            </div>

            {scope === "customer" && (
              <CustomerPickerTable
                selectedIds={
                  customerId ? new Set([customerId]) : new Set<string>()
                }
                // Single-select: the declaration covers ONE customer (Q1).
                onToggle={(id) =>
                  setCustomerId((prev) => (prev === id ? null : id))
                }
              />
            )}
            {scope === "products" && (
              <ProductPickerTable
                selectedIds={selectedProductIds}
                onToggle={(id) =>
                  setSelectedProductIds((prev) => flipMember(prev, id))
                }
              />
            )}
            {scope === "general" && (
              <div className="flex flex-col gap-4">
                <div>
                  <p className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-widest text-grey5">
                    {translate("MC_CS_CONF_V2_GENERAL_ADDRESSEE")}
                  </p>
                  {!customerId && (
                    <p className="mb-2 text-[12.5px] text-grey5">
                      {translate("MC_CS_CONF_V2_GENERAL_ADDRESSEE_ALL")}
                    </p>
                  )}
                  <CustomerPickerTable
                    selectedIds={
                      customerId ? new Set([customerId]) : new Set<string>()
                    }
                    // Single-select, optional and clearable: clicking the
                    // already-picked customer clears it back to "our customers".
                    onToggle={(id) =>
                      setCustomerId((prev) => (prev === id ? null : id))
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor="conformity-general-description"
                    className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-widest text-grey5"
                  >
                    {translate("MC_CS_CONF_V2_GENERAL_DESCRIPTION_LABEL")}
                  </label>
                  <textarea
                    id="conformity-general-description"
                    rows={3}
                    maxLength={500}
                    value={productDescription}
                    onChange={(e) => setProductDescription(e.target.value)}
                    className="w-full resize-y rounded-md border border-grey4 bg-grey3 px-3 py-2 text-[12.5px] text-white outline-hidden focus:border-yellow1"
                  />
                  <p className="mt-1 text-[11px] text-grey5">
                    {translate("MC_CS_CONF_V2_GENERAL_DESCRIPTION_HINT")}
                  </p>
                  {descriptionTooShort && (
                    <p
                      data-testid="general-description-too-short"
                      className="mt-1 text-[11px] text-warning"
                    >
                      {translate("MC_CS_CONF_V2_GENERAL_DESCRIPTION_TOO_SHORT")}
                    </p>
                  )}
                </div>
              </div>
            )}

            {regulationOptions.data && (
              <div data-testid="conf-regulation-picker" className="mt-5">
                <p className="mb-1.5 text-[10.5px] font-semibold tracking-widest text-grey5 uppercase">
                  {translate("MC_CS_CONF_V2_REGULATIONS_HEADING")}
                </p>
                <InquiryRegulationPicker
                  options={pickerOptions ?? regulationOptions.data}
                  value={regulationKeys ?? []}
                  onChange={setPickedRegulations}
                  coupled
                  notPickableHint="MC_CS_CONF_V2_REG_NOT_PICKABLE"
                  emptyHint="MC_CS_CONF_V2_REG_NONE_PICKED"
                />
              </div>
            )}

            {/* Resolved subjects + per-framework outcome once the preview lands */}
            {activeCount > 0 && (
              <div className="mt-5 flex flex-col gap-4">
                {scopeRefusal ? (
                  <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-4 text-[12.5px] text-white">
                    <AlertTriangle
                      size={14}
                      className="mt-px flex-none text-warning"
                    />
                    <div className="min-w-0 flex-1">
                      {translate("MC_CS_CONF_V2_SCOPE_TOO_LARGE", {
                        count: String(scopeRefusal.count),
                        limit: String(scopeRefusal.limit),
                      })}
                      {/* F9: the copy says "raise the limit in settings", so
                          take them there — same gesture as the toast. */}
                      <button
                        type="button"
                        onClick={() =>
                          navigate(SETTINGS_PATHS.conformityDeclarations)
                        }
                        className="ml-2 border-0 bg-transparent p-0 text-[12px] font-medium text-yellow1 underline underline-offset-2"
                      >
                        {translate("MC_CONF_V2_FIX_OPEN_SETTINGS")}
                      </button>
                    </div>
                  </div>
                ) : preview.isLoading || sizePending ? (
                  <div className="flex items-center gap-2 px-1 text-[12px] text-grey5">
                    <Loader2 size={12} className="animate-spin" />
                    {translate("MC_CS_CONF_GEN_LOADING_MORE")}
                  </div>
                ) : noMappedProducts ? (
                  <div className="rounded-md border border-grey4 bg-grey2 p-4">
                    <p className="text-[13px] text-white">
                      {translate("MC_CS_CONF_V2_NO_MAPPED_PRODUCTS")}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <AddProductButton
                        onClick={() => setAddProductOpen(true)}
                        translate={translate}
                      />
                      {customerId && (
                        <Link
                          to={`${MC_BASE}/master-data/customers?id=${customerId}`}
                          className="inline-flex items-center gap-1 text-[12px] font-medium text-yellow1 hover:underline"
                        >
                          {translate("MC_CS_CONF_V2_OPEN_CUSTOMER")}
                          <ArrowRight size={11} />
                        </Link>
                      )}
                    </div>
                  </div>
                ) : noFrameworks ? (
                  <div
                    data-testid="conf-no-frameworks"
                    className="rounded-md border border-grey4 bg-grey2 p-4"
                  >
                    <p className="text-[13px] text-white">
                      {translate("MC_CS_CONF_V2_NO_FRAMEWORKS")}
                    </p>
                    {canOpenRegulations && (
                      <Link
                        to={ORG_REGULATIONS_SETTINGS_ROUTE}
                        className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-yellow1 hover:underline"
                      >
                        {translate("MC_CONF_V2_OPEN_REGULATIONS")}
                        <ArrowRight size={11} />
                      </Link>
                    )}
                  </div>
                ) : evaluation ? (
                  <>
                    {scopeWarned > 0 && (
                      <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-[12px] text-white">
                        <AlertTriangle
                          size={13}
                          className="mt-px flex-none text-warning"
                        />
                        <span>
                          {translate("MC_CS_CONF_V2_SCOPE_WARN", {
                            count: String(scopeWarned),
                          })}
                        </span>
                      </div>
                    )}
                    <SubjectList
                      subjects={subjects}
                      translate={translate}
                      onAddProduct={
                        scope === "customer"
                          ? () => setAddProductOpen(true)
                          : undefined
                      }
                    />
                    <FrameworkSummary
                      frameworks={frameworks}
                      translate={translate}
                    />
                  </>
                ) : null}
              </div>
            )}
          </div>

          {/* Right: TO GENERATE card */}
          <aside className="sticky top-4 rounded-md border border-grey4 bg-grey2 p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-grey5">
              {translate("MC_CS_CONF_GEN_TO_GENERATE")}
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-[36px] font-light leading-none text-white">
                {documentCount}
              </span>
              <span className="text-[12px] text-grey5">
                {translate("MC_CS_CONF_GEN_DECLARATIONS")}
              </span>
            </div>
            <p className="mt-1 text-[12px] text-white">
              {scope === "products" && onePerSubject
                ? translate("MC_CS_CONF_V2_DOCS_PER_PRODUCT", {
                    n: documentCount,
                  })
                : translate("MC_CS_CONF_V2_ONE_COMBINED")}
            </p>

            {publishedTemplates.length > 1 && (
              <label className="mt-4 flex items-center justify-between gap-3 border-t border-grey4 py-2.5 text-[12px]">
                <Typography as="small" className="font-normal text-grey5">
                  {translate("MC_CS_CONF_GEN_TEMPLATE")}
                </Typography>
                <Select
                  value={
                    templateKey ??
                    publishedTemplates.find((t) => t.isDefault)?.key ??
                    DEFAULT_TEMPLATE_KEY
                  }
                  onValueChange={setTemplateKey}
                >
                  <SelectTrigger
                    className="h-8 w-52"
                    aria-label={translate("MC_CS_CONF_GEN_TEMPLATE")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {publishedTemplates.map((t) => (
                      <SelectItem key={t.key} value={t.key}>
                        {t.name}
                        {t.isDefault
                          ? ` ${translate("MC_CS_CONF_GEN_TEMPLATE_DEFAULT")}`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            )}

            {scope === "products" && (
              <label className="mt-4 flex items-center justify-between gap-3 border-y border-grey4 py-2.5 text-[12px]">
                <span className="text-grey5">
                  {translate("MC_CS_CONF_V2_ONE_PER_PRODUCT")}
                </span>
                <Switch
                  checked={onePerSubject}
                  onCheckedChange={setOnePerSubject}
                />
              </label>
            )}

            <Button
              className="mt-4 h-9 w-full bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
              disabled={
                !generateTarget ||
                generate.isPending ||
                preview.isLoading ||
                noMappedProducts ||
                noFrameworks ||
                !!scopeRefusal
              }
              onClick={onGenerate}
            >
              {generate.isPending ? (
                <>
                  <Loader2 size={13} className="mr-1.5 animate-spin" />
                  {translate("MC_CS_CONF_GEN_GENERATING")}
                </>
              ) : (
                <>
                  {translate("MC_CS_CONF_GEN_GENERATE", { n: documentCount })}
                  <ArrowRight size={13} className="ml-1.5" />
                </>
              )}
            </Button>
          </aside>
        </div>
      </Step>

      {/* Link a product to this customer (master data) and re-evaluate */}
      <Dialog open={addProductOpen} onOpenChange={setAddProductOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{translate("MC_CS_CONF_V2_ADD_PRODUCT")}</DialogTitle>
            <p className="text-[12px] text-grey5">
              {translate("MC_CS_CONF_V2_ADD_PRODUCT_HINT")}
            </p>
          </DialogHeader>
          <ProductPickerTable
            // Already-mapped products are hidden: re-linking one would wipe
            // its stored customer material number.
            selectedIds={productsToLink}
            hiddenIds={mappedProductIds}
            onToggle={(id) => setProductsToLink((prev) => flipMember(prev, id))}
          />
          <DialogFooter>
            <Button
              variant="outline"
              className="h-9 text-[13px]"
              onClick={() => {
                setAddProductOpen(false)
                setProductsToLink(new Set())
              }}
            >
              {translate("MC_CS_CONF_EDIT_CANCEL")}
            </Button>
            <Button
              className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
              disabled={productsToLink.size === 0 || linkProduct.isPending}
              onClick={onLinkProducts}
            >
              {linkProduct.isPending && (
                <Loader2 size={13} className="mr-1.5 animate-spin" />
              )}
              {translate("MC_CS_CONF_V2_ADD_PRODUCT")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sell-through picker (Q19) — purchased articles sold unchanged */}
      <Dialog open={sellThroughOpen} onOpenChange={setSellThroughOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{translate("MC_CS_CONF_V2_SOLD_AS_IS")}</DialogTitle>
            <p className="text-[12px] text-grey5">
              {translate("MC_CS_CONF_V2_SOLD_AS_IS_HINT")}
            </p>
          </DialogHeader>
          <EntityPickerTable
            selectedIds={sellThroughIds}
            onToggle={(id) => setSellThroughIds((prev) => flipMember(prev, id))}
            useList={useArticleList}
            emptyLabelKey="MC_CS_CONF_GEN_PICKER_EMPTY_PRODUCTS"
            entityLabelKey="MC_MD_REL_LABEL_ARTICLES"
            renderContent={(row) => (
              <EntityListRowContent
                title={row.name}
                meta={
                  <>
                    <span className="font-mono">{row.articleNumber}</span>
                    {row.supplierName && <span>{row.supplierName}</span>}
                  </>
                }
              />
            )}
          />
          <DialogFooter>
            <Button
              className="h-9 bg-yellow1 text-[13px] font-semibold text-black hover:bg-yellow2 disabled:opacity-40"
              disabled={sellThroughIds.size === 0 || sellThrough.isPending}
              onClick={onCreateSellThrough}
            >
              {sellThrough.isPending ? (
                <Loader2 size={13} className="mr-1.5 animate-spin" />
              ) : null}
              {translate("MC_CS_CONF_V2_SOLD_AS_IS_CONFIRM", {
                n: sellThroughIds.size,
              })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

type Translate = ReturnType<typeof useTranslation>["translate"]

function AddProductButton({
  onClick,
  translate,
}: {
  onClick: () => void
  translate: Translate
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md border border-grey4 bg-grey2 px-2.5 py-1 text-[11.5px] font-medium text-white transition-colors hover:border-grey5"
    >
      <Plus size={12} className="text-yellow1" />
      {translate("MC_CS_CONF_V2_ADD_PRODUCT")}
    </button>
  )
}

function SubjectList({
  subjects,
  translate,
  onAddProduct,
}: {
  subjects: { subjectId: string; number: string; name: string | null }[]
  translate: Translate
  /** Customer scope only: link another product to this customer. */
  onAddProduct?: () => void
}) {
  if (subjects.length === 0) return null
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-grey5">
          {translate("MC_CS_CONF_V2_SUBJECTS_HEADING")}
        </p>
        {onAddProduct && (
          <AddProductButton onClick={onAddProduct} translate={translate} />
        )}
      </div>
      <div className="overflow-hidden rounded-md border border-grey4">
        {subjects.map((s, i) => (
          <div
            key={s.subjectId}
            className={cn(
              "flex items-center gap-3 px-3 py-2",
              i > 0 && "border-t border-white/5",
            )}
          >
            <span className="font-mono min-w-[80px] text-[12px] font-medium text-yellow1">
              {s.number}
            </span>
            <span className="truncate text-[13px] text-white">
              {s.name ?? ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function FrameworkSummary({
  frameworks,
  translate,
}: {
  frameworks: ConformityV2Framework[]
  translate: Translate
}) {
  if (frameworks.length === 0) return null
  return (
    <div>
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-grey5">
        {translate("MC_CS_CONF_V2_FRAMEWORKS_HEADING")}
      </p>
      <div className="overflow-hidden rounded-md border border-grey4">
        {frameworks.map((f, i) => {
          const pill = frameworkClassPill(f, translate)
          return (
            <div
              key={f.frameworkKey}
              className={cn(
                "flex items-center gap-3 px-3 py-2",
                i > 0 && "border-t border-white/5",
              )}
            >
              <span className="min-w-0 flex-1 truncate text-[13px] text-white">
                {f.label}
              </span>
              {f.blockedReason ? (
                <span className="flex items-center gap-1.5 text-[11px] text-grey5">
                  <AlertTriangle size={11} className="text-warning" />
                  {frameworkBlockedLabel(f.blockedReason, translate)}
                </span>
              ) : (
                <McPill tone={pill.tone}>{pill.label}</McPill>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
